/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorPlugins } from '../types/EditorPlugins';
import type { ToolbarOptions } from '../types/ToolbarsOptions';

/**
 * Nested arrays represent toolbar groups, which should be separated with the vertical bar `|`.
 */

export type AnyToolbarItem = ToolbarItem | ImageToolbarItem | TableToolbarItem;

export type ToolbarItem = {
	name: string;
	inAdditionalBlockToolbar: boolean;
	inAdditionalBalloonToolbar: boolean;
	removeWhenMenuBarOn: boolean;
	incompatiblePlugins?: Array<string>;
};

export const defaultToolbarOptions: ToolbarOptions = {
	isMainToolbarOn: true,
	isBalloonToolbarOn: false,
	isBlockToolbarOn: false,
	isCollapsable: false,
	isMenuBarUsed: false,
	buttonsOptions: 'essential'
};

export const mainToolbar: Array<Array<ToolbarItem>> = [
	[
		{
			name: 'undo',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'redo',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'previousPage',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'nextPage',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'pageNavigation',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		}
	], [
		{
			name: 'revisionHistory',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'trackChanges',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'comment',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'commentsArchive',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		}
	], [
		{
			name: 'insertMergeField',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'previewMergeFields',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'aiCommands',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'aiAssistant',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'sourceEditing',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'sourceEditingEnhanced',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'importWord',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'exportWord',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'exportPdf',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'showBlocks',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'formatPainter',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'caseChange',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'findAndReplace',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'textPartLanguage',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'fullscreen',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'heading',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'style',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'fontSize',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'fontFamily',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'fontColor',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'fontBackgroundColor',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'bold',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'italic',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'underline',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'strikethrough',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'subscript',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'superscript',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'code',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'removeFormat',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		}
	], [
		{
			name: 'emoji',
			inAdditionalBalloonToolbar: false,
			inAdditionalBlockToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'specialCharacters',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'horizontalLine',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'pageBreak',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'link',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'bookmark',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'insertImage',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'imageUpload',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false,
			incompatiblePlugins: [
				'ImageInsert'
			]
		},
		{
			name: 'insertImageViaUrl',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false,
			incompatiblePlugins: [
				'ImageInsert',
				'ImageUpload'
			]
		},
		{
			name: 'ckbox',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'uploadcare',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'mediaEmbed',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'insertTable',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'insertTableLayout',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'tableOfContents',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'insertTemplate',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'highlight',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'blockQuote',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'codeBlock',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'htmlEmbed',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		},
		{
			name: 'restrictedEditingException',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: true
		}
	], [
		{
			name: 'alignment',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	], [
		{
			name: 'bulletedList',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'numberedList',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: true,
			removeWhenMenuBarOn: false
		},
		{
			name: 'multiLevelList',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'todoList',
			inAdditionalBlockToolbar: false,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'outdent',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		},
		{
			name: 'indent',
			inAdditionalBlockToolbar: true,
			inAdditionalBalloonToolbar: false,
			removeWhenMenuBarOn: false
		}
	]
];

type ImageToolbarItem = {
	name: string;
	filterCallback?: ( editorPlugins: EditorPlugins ) => boolean;
};

export const imageToolbar: Array<Array<ImageToolbarItem>> = [
	[
		{
			name: 'toggleImageCaption'
		},
		{
			name: 'imageTextAlternative'
		}
	], [
		{
			name: 'imageStyle:inline',
			filterCallback: editorPlugins => editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:wrapText',
			filterCallback: editorPlugins => editorPlugins.includes( 'ImageBlock' ) && editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:breakText',
			filterCallback: editorPlugins => editorPlugins.includes( 'ImageBlock' ) && editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:alignBlockLeft',
			filterCallback: editorPlugins => editorPlugins.includes( 'ImageBlock' ) && !editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:block',
			filterCallback: editorPlugins => editorPlugins.includes( 'ImageBlock' ) && !editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:alignBlockRight',
			filterCallback: editorPlugins => editorPlugins.includes( 'ImageBlock' ) && !editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:alignLeft',
			filterCallback: editorPlugins => !editorPlugins.includes( 'ImageBlock' ) && editorPlugins.includes( 'ImageInline' )
		},
		{
			name: 'imageStyle:alignRight',
			filterCallback: editorPlugins => !editorPlugins.includes( 'ImageBlock' ) && editorPlugins.includes( 'ImageInline' )
		}
	], [
		{
			name: 'resizeImage'
		}
	], [
		{
			name: 'ckboxImageEdit'
		},
		{
			name: 'uploadcareImageEdit'
		}
	]
];

type TableToolbarItem = {
	name: string;
	filterCallback?: ( editorPlugins: EditorPlugins ) => boolean;
};

export const tableToolbar: Array<Array<TableToolbarItem>> = [
	[
		{
			name: 'tableColumn'
		},
		{
			name: 'tableRow'
		},
		{
			name: 'mergeTableCells'
		},
		{
			name: 'tableProperties'
		},
		{
			name: 'tableType',
			// TableProperties includes option to switch table types.
			filterCallback: editorPlugins => editorPlugins.includes( 'TableLayout' ) && !editorPlugins.includes( 'TableProperties' )
		},
		{
			name: 'tableCellProperties'
		},
		{
			name: 'toggleTableCaption'
		}
	]
];
